import { createSlice } from "@reduxjs/toolkit";
import { fetchOrders, createOrderThunk } from "../actions/orderActions";

const initialState = {
  orders: [],
  status: 'idle',
  error: null,
  // New order data structure for checkout process
  orderData: {
    customerId: 0,
    custNumber: 0,
    userId: '',
    emailId: '',
    orderDate: null,
    orderStatus: 'Pending',
    paymentStatus: 'Declined',
    paymentMethod: '',
    paymentGateway: 'ElavonSandbox',
    subtotal: 0,
    subTotalInvoices: 0,
    discount: 0,
    taxAmount: 0,
    taxRate: 0,
    isFreightTaxable: false,
    shippingCharge: 0,
    totalAmount: 0,
    transactionId: '',
    CcType:'',
    shipToNumber: 0,
    shippingDate: null,
    shippingDateValid: false,
    customerPONum: '',
    afterBeforeOn: '2',
    orderItems: []
  },
  loading: false,
  currentOrder: null
}

const orderSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    // Initialize order data from cart
    initializeOrder: (state, action) => {
      const { customerId, custNumber, cartItems, subtotal, delivery, discount, shipToNumber, userId, emailId, subTotalInvoices} = action.payload;
      
      state.orderData = {
        customerId: customerId || 0,
        custNumber: custNumber || 0,
        userId: userId || '',
        emailId: emailId || '',
        orderDate: new Date().toISOString(),
        orderStatus: 'Pending',
        paymentStatus: 'Declined',
        paymentMethod: '',
        paymentGateway: 'ElavonSandbox',
        subtotal: subtotal || 0,
        subTotalInvoices: subTotalInvoices || 0,
        discount: discount || 0,
        taxAmount: 0, // Calculate based on requirements
        shippingCharge: delivery || 0,
        totalAmount: (subtotal || 0) - (discount || 0) + (delivery || 0),
        transactionId: '',
        shipToNumber: shipToNumber || 0,
        shippingDate: null,
        customerPONum: '',
        afterBeforeOn: '2',
        orderItems: cartItems || []
      };
    },
    
    // Update customer information
    updateCustomerInfo: (state, action) => {
      const { customerId, custNumber, userId, emailId } = action.payload;
      if (customerId !== undefined) state.orderData.customerId = customerId;
      if (custNumber !== undefined) state.orderData.custNumber = custNumber;
      if (userId !== undefined) state.orderData.userId = userId;
      if (emailId !== undefined) state.orderData.emailId = emailId;
    },
    
    // Update order status
    updateOrderStatus: (state, action) => {
      state.orderData.orderStatus = action.payload;
    },
    
    // Update payment information
    updatePaymentInfo: (state, action) => {
      const { paymentStatus, paymentMethod, paymentGateway, transactionId, orderStatus,CcType } = action.payload;
      if (paymentStatus !== undefined) state.orderData.paymentStatus = paymentStatus;
      if (paymentMethod !== undefined) state.orderData.paymentMethod = paymentMethod;
      if (paymentGateway !== undefined) state.orderData.paymentGateway = paymentGateway;
      if (transactionId !== undefined) state.orderData.transactionId = transactionId;
      if (orderStatus !== undefined) state.orderData.orderStatus = orderStatus;
      if (CcType !== undefined) state.orderData.CcType = CcType;
    },
    
    // Update shipping information
    updateShippingInfo: (state, action) => {
      const { shipToNumber, shippingCharge, shippingDate, customerPONum, afterBeforeOn, shippingDateValid } = action.payload;
      if (shipToNumber !== undefined) state.orderData.shipToNumber = shipToNumber;
      if (shippingCharge !== undefined) state.orderData.shippingCharge = shippingCharge;
      if (shippingDate !== undefined) state.orderData.shippingDate = shippingDate;
      if (customerPONum !== undefined) state.orderData.customerPONum = customerPONum;
      if (afterBeforeOn !== undefined) state.orderData.afterBeforeOn = afterBeforeOn;
      if (shippingDateValid !== undefined) state.orderData.shippingDateValid = shippingDateValid;

      // Recalculate total
      state.orderData.totalAmount = 
        state.orderData.subtotal - 
        state.orderData.discount + 
        state.orderData.shippingCharge + 
        state.orderData.taxAmount;
    },
    
    // Calculate tax based on shipping address
    calculateTax: (state, action) => {
      const { taxRate, istaxDelivery, kitTotal, itemTotal, groupItemTotal, shippingCharge } = action.payload;
      
      // Store tax rate and freight taxable flag
      state.orderData.taxRate = taxRate || 0;
      state.orderData.isFreightTaxable = istaxDelivery === 'Y';
      
      if (taxRate !== undefined && taxRate > 0) {
        // Base taxable amount (items + kits + groupItems)
        const baseTaxableAmount = (kitTotal || 0) + (itemTotal || 0) + (groupItemTotal || 0);
        
        // If freight is taxable, include shipping charge
        const taxableAmount = (istaxDelivery === 'Y')
          ? baseTaxableAmount + (shippingCharge || 0)
          : baseTaxableAmount;
        
        // Calculate tax (taxRate is already a decimal, e.g., 0.75 for 75%)
        state.orderData.taxAmount = taxableAmount * taxRate;
      } else {
        state.orderData.taxAmount = 0;
      }
      
      // Recalculate total
      state.orderData.totalAmount = 
        state.orderData.subtotal - 
        state.orderData.discount + 
        state.orderData.shippingCharge + 
        state.orderData.taxAmount;
    },
    
    // Update financial information
    updateOrderTotals: (state, action) => {
      const { subtotal, discount, taxAmount, shippingCharge, subTotalInvoices  } = action.payload;
      if (subtotal !== undefined) state.orderData.subtotal = subtotal;
      if (discount !== undefined) state.orderData.discount = discount;
      if (taxAmount !== undefined) state.orderData.taxAmount = taxAmount;
      if (shippingCharge !== undefined) state.orderData.shippingCharge = shippingCharge;
      if (subTotalInvoices !== undefined) state.orderData.subTotalInvoices = subTotalInvoices;
      // Recalculate total
      state.orderData.totalAmount = 
        state.orderData.subtotal - 
        state.orderData.discount + 
        state.orderData.shippingCharge + 
        state.orderData.taxAmount;
    },
    
    // Update order items
    updateOrderItems: (state, action) => {
      state.orderData.orderItems = action.payload;
    },
    
    // Add single order item
    addOrderItem: (state, action) => {
      state.orderData.orderItems.push(action.payload);
    },
    
    // Remove order item
    removeOrderItem: (state, action) => {
      const itemId = action.payload;
      state.orderData.orderItems = state.orderData.orderItems.filter(
        (item, index) => index !== itemId
      );
    },
    
    // Clear order data
    clearOrder: (state) => {
      state.orderData = {
        customerId: 0,
        custNumber: 0,
        userId: '',
        emailId: '',
        orderDate: null,
        orderStatus: 'Pending',
        paymentStatus: 'Declined',
        paymentMethod: '',
        paymentGateway: '',
        subtotal: 0,
        discount: 0,
        taxAmount: 0,
        shippingCharge: 0,
        totalAmount: 0,
        transactionId: '',
        CcType:'',
        shipToNumber: 0,
        shippingDate: null,
        shippingDateValid: false,
        orderItems: []
      };
      state.currentOrder = null;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Existing fetch orders cases
      .addCase(fetchOrders.pending, (state, action) => {
        state.status = 'loading';
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.orders = action.payload;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      // New create order cases
      .addCase(createOrderThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createOrderThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentOrder = action.payload;
        state.error = null;
        // Optionally add to orders list
        state.orders.push(action.payload);
      })
      .addCase(createOrderThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const {
  initializeOrder,
  updateCustomerInfo,
  updateOrderStatus,
  updatePaymentInfo,
  updateShippingInfo,
  updateOrderTotals,
  updateOrderItems,
  addOrderItem,
  removeOrderItem,
  clearOrder,
  calculateTax
} = orderSlice.actions;

export default orderSlice.reducer;
